import type { FC } from "react"
import type { ComponentWithAs, IconProps } from "@chakra-ui/react"
import {
  Button,
  HStack,
  Icon,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Stack,
  Text,
  useDisclosure,
} from "@chakra-ui/react"
import { useFormikContext } from "formik"
import { ArrowForwardIcon, RepeatIcon } from "@chakra-ui/icons"
import { AccountId } from "caip"
import type { AbiItem } from "web3-utils"

import TransferNativeAssetAction from "createProposal/action/TransferNativeAssetAction"
import CustomAction from "createProposal/action/CustomAction"
import type { CreateProposalFormValues } from "createProposal/types/form"
import SwapAction from "createProposal/action/SwapAction"
import { getChain } from "web3/helpers/chains"
import { getTallyRewardRecipient } from "createProposal/helpers/reward"
import { LayerPlus } from "ui/components/icons/font-awesome/LayerPlus"
import {
  FeatureFlag,
  useOrganization,
} from "organization/providers/OrganizationProvider"
import type { AddressBookContract } from "createProposal/constants/address-book-contracts"
import { getAddressBookContracts } from "createProposal/constants/address-book-contracts"
import { useCreateProposal } from "createProposal/providers/CreateProposalProvider"
import Star from "ui/components/icons/font-awesome/Star"
import type { AddressBookBridgeContract } from "createProposal/constants/address-book-bridge-contracts"
import { getAddressBookBridgeContracts } from "createProposal/constants/address-book-bridge-contracts"
import { Bridge } from "ui/components/icons/font-awesome/Bridge"
import type { AbiItemWithValues } from "createProposal/types/abi"

type RecipeSelectorProps = {
  index: number
  timelockId: string | undefined | null
  chainId: string
}

const RecipeSelector: FC<RecipeSelectorProps> = ({
  index,
  timelockId,
  chainId,
}) => {
  const { setFieldValue } = useFormikContext<CreateProposalFormValues>()
  const chain = getChain(chainId)
  const cowswapSupport = chain?.cowswapSupport
  const milkmanContract = chain?.milkmanContract
  const { isFeatureFlagOn } = useOrganization()
  const displayTransferAction = !isFeatureFlagOn(FeatureFlag.HideTransferAction)
  const { governanceId } = useCreateProposal()
  const addressBookContracts = getAddressBookContracts(governanceId)
  const addressBookBridgeContracts = getAddressBookBridgeContracts(governanceId)

  const { organization } = useOrganization()

  const handleClickTransferTokensRecipe = () =>
    setFieldValue(
      `actions.${index}`,
      new TransferNativeAssetAction({
        tallyRewardRecipient: getTallyRewardRecipient(chainId),
      }),
    )
  const handleClickSwapRecipe = () => {
    if (!timelockId || !milkmanContract) {
      return
    }

    const { address: timelockAddress } = new AccountId(timelockId)
    const { address: milkmanContractAddress } = new AccountId(milkmanContract)

    setFieldValue(
      `actions.${index}`,
      new SwapAction({
        milkmanContractAddress,
        to: timelockAddress,
      }),
    )
  }
  const handleClickCustomRecipe = () =>
    setFieldValue(`actions.${index}`, new CustomAction({ value: "0" }))

  const handleClickCustomRecipeWithParams = ({
    targetAddress,
    abiMethods,
  }: {
    targetAddress: string
    abiMethods?: AbiItem[]
  }) => {
    if (abiMethods && abiMethods.length > 0) {
      const customAction = new CustomAction({
        targetAddress,
        value: "0",
        selectedAbiMethod: abiMethods[0]
          ? (abiMethods[0] as unknown as AbiItemWithValues)
          : undefined,
      }).setAbiMethods(abiMethods)

      setFieldValue(`actions.${index}`, customAction)
    } else {
      const customAction = new CustomAction({
        targetAddress,
        value: "0",
      })
      setFieldValue(`actions.${index}`, customAction)
    }
  }

  return (
    <Stack>
      {displayTransferAction ? (
        <RecipeButton
          icon={() => <ArrowForwardIcon color="purple.500" />}
          name="Transfer tokens"
          onClick={handleClickTransferTokensRecipe}
        />
      ) : null}
      {cowswapSupport && (
        <RecipeButton
          icon={() => <RepeatIcon color="purple.500" />}
          name="Swap"
          onClick={handleClickSwapRecipe}
        />
      )}
      {addressBookContracts && organization ? (
        <AddressBookContractsButton
          contracts={addressBookContracts}
          icon={() => <Star color="purple.500" />}
          name={organization.name}
          onClick={handleClickCustomRecipeWithParams}
        />
      ) : null}
      {addressBookBridgeContracts && organization ? (
        <AddressBookBridgeContractsButton
          contracts={addressBookBridgeContracts}
          icon={() => <Bridge color="purple.500" />}
          name="Bridge wrapped action(s)"
          onClick={handleClickCustomRecipeWithParams}
        />
      ) : null}
      <RecipeButton
        icon={() => <LayerPlus fill="purple.500" />}
        name="Custom action"
        onClick={handleClickCustomRecipe}
      />
    </Stack>
  )
}

type RecipeButtonProps = {
  name: string
  icon: ComponentWithAs<"svg", IconProps>
  onClick: () => void
}

const RecipeButton: FC<RecipeButtonProps> = ({ name, icon, onClick }) => {
  return (
    <HStack
      align="center"
      as="button"
      border="gray.light"
      h={14}
      px={4}
      spacing={2}
      type="button"
      w={{ base: "full", sm: "80" }}
      onClick={onClick}
    >
      <Icon as={icon} boxSize={4} />
      <Text textStyle="body.bold.lg">{name}</Text>
    </HStack>
  )
}

type AddressBookContractsButtonProps = {
  contracts: AddressBookContract[]
  icon: ComponentWithAs<"svg", IconProps>
  name: string
  onClick: ({ targetAddress }: { targetAddress: string }) => void
}
const AddressBookContractsButton: FC<AddressBookContractsButtonProps> = ({
  contracts,
  icon,
  name,
  onClick,
}) => {
  const contractsModalDisclouse = useDisclosure()

  return (
    <>
      <HStack
        align="center"
        as="button"
        border="gray.light"
        h={14}
        px={4}
        spacing={2}
        type="button"
        w={{ base: "full", sm: "80" }}
        onClick={contractsModalDisclouse.onOpen}
      >
        <Icon as={icon} boxSize={4} />
        <Text align="left" textStyle="body.bold.lg" width="100%">
          {name} contracts
        </Text>
      </HStack>
      <Modal
        closeOnOverlayClick={false}
        isOpen={contractsModalDisclouse.isOpen}
        onClose={contractsModalDisclouse.onClose}
      >
        <ModalOverlay backdropFilter="blur(5px)" />
        <ModalContent top={{ base: "unset", lg: "100px" }}>
          <ModalHeader
            borderBottomColor="gray.100"
            borderBottomWidth={1}
            p={4}
            textStyle="h5"
          >
            <Text>{name} contracts</Text>
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody
            css={{
              "&::-webkit-scrollbar": {
                width: "8px",
                marginRight: "8px",
              },
              "&::-webkit-scrollbar-track": {
                width: "8px",
                marginRight: "8px",
              },
              "&::-webkit-scrollbar-thumb": {
                background: "gray",
                borderRadius: "24px",
              },
            }}
            maxHeight="300px"
            mr={2}
            overflowY="auto"
            px={4}
            py={4}
          >
            <Stack spacing={4}>
              <Stack direction="column" justify="space-between">
                {contracts.map((contract, idx) => (
                  <Button
                    key={`contract-btn-${idx}`}
                    height={contract.description ? "100%" : "50px"}
                    maxWidth="400px" // adjust as needed
                    variant="outline"
                    width="100%"
                    onClick={() => {
                      onClick({
                        targetAddress: contract.address,
                      })
                    }}
                  >
                    <Stack flexWrap="wrap" py={4} width="100%">
                      <HStack align="center">
                        <Icon as={LayerPlus} fill="purple.500" />
                        <Text fontSize="md">{contract.name}</Text>
                      </HStack>
                      {contract.description ? (
                        <Text
                          align="left"
                          fontWeight="normal"
                          whiteSpace="normal"
                        >
                          {contract.description}
                        </Text>
                      ) : null}
                    </Stack>
                  </Button>
                ))}
              </Stack>
            </Stack>
          </ModalBody>
        </ModalContent>
      </Modal>
    </>
  )
}

type AddressBookBridgeContractsButtonProps = {
  contracts: AddressBookBridgeContract[]
  icon: ComponentWithAs<"svg", IconProps>
  name: string
  onClick: ({
    targetAddress,
    abiMethods,
  }: {
    targetAddress: string
    abiMethods: AbiItem[]
  }) => void
}
const AddressBookBridgeContractsButton: FC<
  AddressBookBridgeContractsButtonProps
> = ({ contracts, icon, name, onClick }) => {
  const contractsModalDisclouse = useDisclosure()

  return (
    <>
      <HStack
        align="center"
        as="button"
        border="gray.light"
        h={14}
        px={4}
        spacing={2}
        type="button"
        w={{ base: "full", sm: "80" }}
        onClick={contractsModalDisclouse.onOpen}
      >
        <Icon as={icon} boxSize={4} />
        <Text textStyle="body.bold.lg">{name}</Text>
      </HStack>
      <Modal
        closeOnOverlayClick={false}
        isOpen={contractsModalDisclouse.isOpen}
        onClose={contractsModalDisclouse.onClose}
      >
        <ModalOverlay backdropFilter="blur(5px)" />
        <ModalContent top={{ base: "unset", lg: "100px" }}>
          <ModalHeader borderBottomColor="gray.100" p={4} textStyle="h5">
            <Text>Select network</Text>
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody
            css={{ scrollbarWidth: "auto", scrollbarColor: "gray lightgray" }}
            maxHeight="300px"
            overflowY="scroll"
            px={4}
            py={4}
          >
            <Stack spacing={4}>
              <Stack direction="column" justify="space-between">
                {contracts.map((contract, idx) => (
                  <Button
                    key={`contract-btn-${idx}`}
                    height="100%"
                    maxWidth="400px" // adjust as needed
                    variant="outline"
                    width="100%"
                    onClick={() => {
                      onClick({
                        targetAddress: contract.address,
                        abiMethods: contract?.abiMethods ?? [],
                      })
                    }}
                  >
                    <Stack flexWrap="wrap" py={4} width="100%">
                      <HStack align="center">
                        <Icon as={contract.icon ?? LayerPlus} h={5} w={5} />
                        <Text fontSize="md">{contract.chainName}</Text>
                      </HStack>
                    </Stack>
                  </Button>
                ))}
              </Stack>
            </Stack>
          </ModalBody>
        </ModalContent>
      </Modal>
    </>
  )
}

export default RecipeSelector
